import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { ecommerceService } from '../../services/ecommerceService';

// Async thunks for cart operations
export const fetchCart = createAsyncThunk(
  'ecommerceCart/fetchCart',
  async (_, { rejectWithValue }) => {
    try {
      const response = await ecommerceService.getCart();
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const addToCart = createAsyncThunk(
  'ecommerceCart/addToCart',
  async ({ productId, variantId = null, quantity = 1 }, { rejectWithValue }) => {
    try {
      const response = await ecommerceService.addToCart(productId, variantId, quantity);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const updateCartItem = createAsyncThunk(
  'ecommerceCart/updateCartItem',
  async ({ itemId, quantity }, { rejectWithValue }) => {
    try {
      const response = await ecommerceService.updateCartItem(itemId, quantity);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const removeFromCart = createAsyncThunk(
  'ecommerceCart/removeFromCart',
  async (itemId, { rejectWithValue }) => {
    try {
      const response = await ecommerceService.removeFromCart(itemId);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const applyCoupon = createAsyncThunk(
  'ecommerceCart/applyCoupon',
  async (couponCode, { rejectWithValue }) => {
    try {
      const response = await ecommerceService.applyCoupon(couponCode);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const removeCoupon = createAsyncThunk(
  'ecommerceCart/removeCoupon',
  async (_, { rejectWithValue }) => {
    try {
      const response = await ecommerceService.removeCoupon();
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const clearCart = createAsyncThunk(
  'ecommerceCart/clearCart',
  async (_, { rejectWithValue }) => {
    try {
      const response = await ecommerceService.clearCart();
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

const initialState = {
  cart: null,
  items: [],
  itemCount: 0,
  subtotal: 0,
  discountAmount: 0,
  taxAmount: 0,
  shippingCost: 0,
  totalAmount: 0,
  appliedCoupon: null,
  loading: false,
  error: null,
  successMessage: null,
  cartDrawerOpen: false,
};

const ecommerceCartSlice = createSlice({
  name: 'ecommerceCart',
  initialState,
  reducers: {
    toggleCartDrawer: (state, action) => {
      state.cartDrawerOpen = action.payload !== undefined ? action.payload : !state.cartDrawerOpen;
    },
    clearError: (state) => {
      state.error = null;
    },
    clearSuccessMessage: (state) => {
      state.successMessage = null;
    },
    resetCart: (state) => {
      return initialState;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch Cart
      .addCase(fetchCart.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchCart.fulfilled, (state, action) => {
        state.loading = false;
        state.cart = action.payload;
        state.items = action.payload.items || [];
        state.itemCount = action.payload.item_count || 0;
        state.subtotal = parseFloat(action.payload.subtotal || 0);
        state.discountAmount = parseFloat(action.payload.discount_amount || 0);
        state.taxAmount = parseFloat(action.payload.tax_amount || 0);
        state.shippingCost = parseFloat(action.payload.shipping_cost || 0);
        // API's CartSerializer exposes the grand total as `total` (fall back to
        // `total_amount` for any legacy/checkout payloads that use that name).
        state.totalAmount = parseFloat(action.payload.total ?? action.payload.total_amount ?? 0);
        state.appliedCoupon = action.payload.applied_coupon || null;
      })
      .addCase(fetchCart.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Add to Cart
      .addCase(addToCart.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(addToCart.fulfilled, (state, action) => {
        state.loading = false;
        state.cart = action.payload;
        state.items = action.payload.items || [];
        state.itemCount = action.payload.item_count || 0;
        state.subtotal = parseFloat(action.payload.subtotal || 0);
        state.discountAmount = parseFloat(action.payload.discount_amount || 0);
        state.taxAmount = parseFloat(action.payload.tax_amount || 0);
        state.shippingCost = parseFloat(action.payload.shipping_cost || 0);
        // API's CartSerializer exposes the grand total as `total` (fall back to
        // `total_amount` for any legacy/checkout payloads that use that name).
        state.totalAmount = parseFloat(action.payload.total ?? action.payload.total_amount ?? 0);
        state.appliedCoupon = action.payload.applied_coupon || null;
        state.successMessage = 'Item added to cart';
        state.cartDrawerOpen = true;
      })
      .addCase(addToCart.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Update Cart Item
      .addCase(updateCartItem.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateCartItem.fulfilled, (state, action) => {
        state.loading = false;
        state.cart = action.payload;
        state.items = action.payload.items || [];
        state.itemCount = action.payload.item_count || 0;
        state.subtotal = parseFloat(action.payload.subtotal || 0);
        state.discountAmount = parseFloat(action.payload.discount_amount || 0);
        state.taxAmount = parseFloat(action.payload.tax_amount || 0);
        state.shippingCost = parseFloat(action.payload.shipping_cost || 0);
        // API's CartSerializer exposes the grand total as `total` (fall back to
        // `total_amount` for any legacy/checkout payloads that use that name).
        state.totalAmount = parseFloat(action.payload.total ?? action.payload.total_amount ?? 0);
        state.appliedCoupon = action.payload.applied_coupon || null;
      })
      .addCase(updateCartItem.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Remove from Cart
      .addCase(removeFromCart.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(removeFromCart.fulfilled, (state, action) => {
        state.loading = false;
        state.cart = action.payload;
        state.items = action.payload.items || [];
        state.itemCount = action.payload.item_count || 0;
        state.subtotal = parseFloat(action.payload.subtotal || 0);
        state.discountAmount = parseFloat(action.payload.discount_amount || 0);
        state.taxAmount = parseFloat(action.payload.tax_amount || 0);
        state.shippingCost = parseFloat(action.payload.shipping_cost || 0);
        // API's CartSerializer exposes the grand total as `total` (fall back to
        // `total_amount` for any legacy/checkout payloads that use that name).
        state.totalAmount = parseFloat(action.payload.total ?? action.payload.total_amount ?? 0);
        state.appliedCoupon = action.payload.applied_coupon || null;
        state.successMessage = 'Item removed from cart';
      })
      .addCase(removeFromCart.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Apply Coupon
      .addCase(applyCoupon.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(applyCoupon.fulfilled, (state, action) => {
        state.loading = false;
        const cart = action.payload.cart;
        state.cart = cart;
        state.items = cart.items || [];
        state.itemCount = cart.item_count || 0;
        state.subtotal = parseFloat(cart.subtotal || 0);
        state.discountAmount = parseFloat(cart.discount_amount || 0);
        state.taxAmount = parseFloat(cart.tax_amount || 0);
        state.shippingCost = parseFloat(cart.shipping_cost || 0);
        state.totalAmount = parseFloat(cart.total ?? cart.total_amount ?? 0);
        state.appliedCoupon = cart.applied_coupon || null;
        state.successMessage = action.payload.message || 'Coupon applied successfully';
      })
      .addCase(applyCoupon.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.error || action.payload;
      })

      // Remove Coupon
      .addCase(removeCoupon.fulfilled, (state, action) => {
        state.cart = action.payload;
        state.items = action.payload.items || [];
        state.discountAmount = parseFloat(action.payload.discount_amount || 0);
        state.totalAmount = parseFloat(action.payload.total ?? action.payload.total_amount ?? 0);
        state.appliedCoupon = null;
        state.successMessage = 'Coupon removed';
      })

      // Clear Cart
      .addCase(clearCart.fulfilled, (state) => {
        state.cart = null;
        state.items = [];
        state.itemCount = 0;
        state.subtotal = 0;
        state.discountAmount = 0;
        state.taxAmount = 0;
        state.shippingCost = 0;
        state.totalAmount = 0;
        state.appliedCoupon = null;
        state.successMessage = 'Cart cleared';
      });
  },
});

export const {
  toggleCartDrawer,
  clearError,
  clearSuccessMessage,
  resetCart,
} = ecommerceCartSlice.actions;

export default ecommerceCartSlice.reducer;
